import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Shield, Eye, EyeOff, Pencil, X, Star } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input, Select } from '../components/ui';

interface Seller {
  id?: string;
  _id?: string;
  user: string;
  type: string;
  name: string;
  logo: string;
  official: boolean;
  status: string;
  rating?: number;
  noOfRating?: number;
  isPinned?: boolean;
  isPlatformStore?: boolean;
  promotionActive?: boolean;
  promotionStartsAt?: string;
  promotionExpiresAt?: string;
  promotionActivatedAt?: string;
  promotionTier?: number;
  createdAt?: string;
  updatedAt?: string;
}

const toDatetimeLocal = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

interface User {
  id?: string;
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  banned: boolean;
  banReason?: string;
  banExpires?: string;
  verified: boolean;
  createdAt: string;
  phoneNumber?: string;
  nationality?: string;
  currentLocation?: string;
  profileImageUrl?: string;
  updatedAt?: string;
}

interface UsersResponse {
  users: User[];
  total: number;
  totalPages: number;
  currentPage: number;
}

const Users: React.FC = () => {
  const { t } = useTranslation();
  const { admin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterBanned, setFilterBanned] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);
  const [detailsSeller, setDetailsSeller] = useState<Seller | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState<Partial<User>>({});
  const [editSeller, setEditSeller] = useState<Partial<Seller>>({});
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordNew, setResetPasswordNew] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetPasswordSaving, setResetPasswordSaving] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileImageUploading, setProfileImageUploading] = useState(false);
  const [profileImagePreviewUrl, setProfileImagePreviewUrl] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(filterRole && { role: filterRole }),
        ...(filterBanned && { banned: filterBanned }),
      });

      const response = await api.get(`/admin/users?${params}`);
      const data: UsersResponse = response.data.data;
      
      // Normalize users: convert _id to id if needed
      const normalizedUsers = data.users.map((user: any) => ({
        ...user,
        id: user.id || user._id?.toString() || user._id,
      }));
      
      setUsers(normalizedUsers);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterRole, filterBanned]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm, filterRole, filterBanned, fetchUsers]);

  const handleBanUser = async (user: User, banned: boolean) => {
    try {
      // Get user ID (support both id and _id)
      const userId = user.id || user._id;
      if (!userId) {
        console.error('User ID is missing');
        throw new Error('User ID is missing');
      }

      const payload = {
        banned,
        ...(banned && banReason && { banReason }),
      };

      await api.put(`/admin/users/${userId}/ban`, payload);
      setShowBanModal(false);
      setBanReason('');
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user ban status:', error);
    }
  };

  const openBanModal = (user: User) => {
    setSelectedUser(user);
    setShowBanModal(true);
  };

  const currentAdminId = admin?.id;
  const isViewingSelf = detailsUser && (String(detailsUser.id ?? detailsUser._id) === String(currentAdminId ?? ''));
  const canEditCurrentUser = !isViewingSelf || admin?.role === 'SUPER_ADMIN';

  const canEditUser = (user: User) => {
    const uid = String(user.id ?? user._id);
    return uid !== String(currentAdminId ?? '') || admin?.role === 'SUPER_ADMIN';
  };

  const openDetailsModal = async (user: User, asEdit = false) => {
    const userId = user.id || user._id;
    if (!userId) return;
    const allowEdit = canEditUser(user);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    setEditMode(asEdit && allowEdit);
    setDetailsUser(null);
    setDetailsSeller(null);
    setDetailsError('');
    try {
      const res = await api.get(`/admin/users/${userId}`);
      const data = res.data?.data || res.data;
      const u = data.user || data;
      let s = data.seller ?? null;
      if (!s && u?.seller && typeof u.seller === 'object') {
        s = u.seller;
        const { seller: _nested, ...userWithoutSeller } = u;
        setDetailsUser(userWithoutSeller);
      } else {
        setDetailsUser(u);
      }
      setDetailsSeller(s);
      if (profileImagePreviewUrl) {
        URL.revokeObjectURL(profileImagePreviewUrl);
        setProfileImagePreviewUrl(null);
      }
      setEditUser({
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email,
        phoneNumber: u.phoneNumber ?? '',
        nationality: u.nationality ?? '',
        currentLocation: u.currentLocation ?? '',
        profileImageUrl: u.profileImageUrl ?? '',
        role: u.role,
        verified: u.verified,
      });
      setEditSeller(s ? {
        name: s.name,
        logo: s.logo ?? '',
        type: s.type,
        official: s.official,
        status: s.status ?? '',
        promotionActive: s.promotionActive ?? false,
        promotionStartsAt: toDatetimeLocal(s.promotionStartsAt),
        promotionExpiresAt: toDatetimeLocal(s.promotionExpiresAt),
        promotionTier: s.promotionTier ?? 1,
      } : {});
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string }; status?: number } };
      console.error('Failed to load user details', e);
      setDetailsError(
        err.response?.data?.message ||
          (err.response?.status === 404
            ? 'API introuvable — redémarrez le backend (VOG_ecom).'
            : 'Impossible de charger les détails utilisateur.')
      );
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetailsModal = () => {
    if (profileImagePreviewUrl) URL.revokeObjectURL(profileImagePreviewUrl);
    setProfileImagePreviewUrl(null);
    setShowDetailsModal(false);
    setDetailsUser(null);
    setDetailsSeller(null);
    setEditMode(false);
    setEditUser({});
    setEditSeller({});
    setShowResetPassword(false);
    setResetPasswordNew('');
    setResetPasswordConfirm('');
    setResetPasswordError('');
  };

  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const objectUrl = URL.createObjectURL(file);
    setProfileImagePreviewUrl(objectUrl);
    setProfileImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data: resBody } = await api.post<{ data?: { fileUrl?: string } }>('upload-single-file', formData);
      const fileUrl = resBody?.data?.fileUrl;
      if (fileUrl) {
        setEditUser((u) => ({ ...u, profileImageUrl: fileUrl }));
        URL.revokeObjectURL(objectUrl);
        setProfileImagePreviewUrl(null);
      }
    } catch (err) {
      console.error('Profile image upload failed', err);
      URL.revokeObjectURL(objectUrl);
      setProfileImagePreviewUrl(null);
    } finally {
      setProfileImageUploading(false);
      e.target.value = '';
    }
  };

  const handleResetPassword = async () => {
    if (resetPasswordNew.length < 6) {
      setResetPasswordError(t('validation.passwordMin', { ns: 'users' }));
      return;
    }
    if (resetPasswordNew !== resetPasswordConfirm) {
      setResetPasswordError(t('validation.passwordMismatch', { ns: 'users' }));
      return;
    }
    const userId = detailsUser ? (detailsUser.id ?? (detailsUser as any)._id) : null;
    if (!userId) return;
    setResetPasswordError('');
    setResetPasswordSaving(true);
    try {
      await api.put(`/admin/users/${userId}/reset-password`, { newPassword: resetPasswordNew });
      setShowResetPassword(false);
      setResetPasswordNew('');
      setResetPasswordConfirm('');
    } catch (e: any) {
      setResetPasswordError(e.response?.data?.message || t('messages.resetPasswordFailed', { ns: 'users' }));
    } finally {
      setResetPasswordSaving(false);
    }
  };

  const handleSaveDetails = async () => {
    const userId = detailsUser ? (detailsUser.id ?? (detailsUser as any)._id) : null;
    if (!userId) return;
    setSaving(true);
    try {
      await api.put(`/admin/users/${userId}`, editUser);
      if (detailsSeller && Object.keys(editSeller).length) {
        const sellerPayload = {
          ...editSeller,
          promotionStartsAt: editSeller.promotionStartsAt
            ? new Date(String(editSeller.promotionStartsAt)).toISOString()
            : null,
          promotionExpiresAt: editSeller.promotionExpiresAt
            ? new Date(String(editSeller.promotionExpiresAt)).toISOString()
            : null,
        };
        await api.put(`/admin/users/${userId}/seller`, sellerPayload);
      }
      const res = await api.get(`/admin/users/${userId}`);
      const data = res.data?.data || res.data;
      setDetailsUser(data.user || data);
      setDetailsSeller(data.seller ?? null);
      setEditMode(false);
      fetchUsers();
    } catch (e) {
      console.error('Failed to save user details', e);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'users' })}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'users' })}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-sm text-gray-500">
            {t('totalUsers', { ns: 'users' })}: {total}
          </div>
          <Link
            to="/boutique-promotions"
            className="inline-flex items-center gap-2 text-sm font-medium text-amber-800 hover:text-amber-900"
          >
            <Star size={16} />
            {t('sections.openPromotionsPage', { ns: 'users' })}
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t('search', { ns: 'users' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
          </div>

          {/* Role Filter */}
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="input-field"
          >
            <option value="">{t('filters.allRoles', { ns: 'users' })}</option>
            <option value="user">{t('roles.user', { ns: 'users' })}</option>
            <option value="seller">{t('roles.seller', { ns: 'users' })}</option>
          </select>

          {/* Ban Status Filter */}
          <select
            value={filterBanned}
            onChange={(e) => setFilterBanned(e.target.value)}
            className="input-field"
          >
            <option value="">{t('filters.allStatus', { ns: 'users' })}</option>
            <option value="false">{t('status.active', { ns: 'common' })}</option>
            <option value="true">{t('status.banned', { ns: 'users' })}</option>
          </select>

          {/* Clear Filters */}
          <button
            onClick={() => {
              setSearchTerm('');
              setFilterRole('');
              setFilterBanned('');
              setCurrentPage(1);
            }}
            className="btn-outline"
          >
            {t('filters.clear', { ns: 'common' })}
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.user', { ns: 'users' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.role', { ns: 'users' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.status', { ns: 'users' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('fields.joined', { ns: 'users' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.actions', { ns: 'common' })}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {t('table.noData', { ns: 'common' })}
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id || user._id || `user-${index}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium">
                            {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge-${user.role === 'seller' ? 'yellow' : 'gray'}`}>
                        {t(`roles.${user.role}`, { ns: 'users' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className={`badge-${user.banned ? 'red' : 'green'}`}>
                          {user.banned ? t('status.banned', { ns: 'users' }) : t('status.active', { ns: 'common' })}
                        </span>
                        {user.verified && (
                          <div title={t('status.verified', { ns: 'users' })}>
                            <Shield size={16} className="text-green-500" />
                          </div>
                        )}
                      </div>
                      {user.banned && user.banReason && (
                        <div className="text-xs text-red-600 mt-1">
                          {t('banReason', { ns: 'users' })}: {user.banReason}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openDetailsModal(user, false)}
                          className="text-gray-500 hover:text-primary-600 p-1"
                          title={t('actions.view', { ns: 'users' })}
                        >
                          <Eye size={16} />
                        </button>
                        {canEditUser(user) && (
                          <button
                            onClick={() => openDetailsModal(user, true)}
                            className="text-gray-500 hover:text-primary-600 p-1"
                            title={t('actions.edit', { ns: 'users' })}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        {String(user.id ?? user._id) !== String(currentAdminId ?? '') && (
                          <button
                            onClick={() => openBanModal(user)}
                            className={`btn-${user.banned ? 'primary' : 'secondary'} text-xs py-1 px-2`}
                          >
                            {user.banned ? t('actions.unban', { ns: 'users' }) : t('actions.ban', { ns: 'users' })}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {t('table.showing', { ns: 'common' })} {((currentPage - 1) * 10) + 1} {t('table.to', { ns: 'common' })} {Math.min(currentPage * 10, total)} {t('table.of', { ns: 'common' })} {total} {t('table.results', { ns: 'common' })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.previous', { ns: 'common' })}
              </button>
              <span className="text-sm text-gray-700">
                {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.next', { ns: 'common' })}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User/Seller Details Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">{t('userDetails', { ns: 'users' })}</h2>
              <div className="flex items-center gap-2">
                {editMode && canEditCurrentUser && (
                  <Button onClick={handleSaveDetails} disabled={saving} size="sm">
                    {saving ? '...' : t('buttons.save', { ns: 'common' })}
                  </Button>
                )}
                {!editMode && detailsUser && canEditCurrentUser && (
                  <Button onClick={() => setEditMode(true)} variant="outline" size="sm" icon={Pencil}>
                    {t('actions.edit', { ns: 'users' })}
                  </Button>
                )}
                <button onClick={closeDetailsModal} className="p-1 text-gray-500 hover:text-gray-700">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              {detailsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
                </div>
              ) : detailsError ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
                  {detailsError}
                </div>
              ) : detailsUser ? (
                <>
                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900 border-b pb-2">{t('fields.user', { ns: 'users' })}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {editMode ? (
                        <>
                          <Input label={t('fields.firstName', { ns: 'users' })} value={editUser.firstName ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, firstName: e.target.value }))} />
                          <Input label={t('fields.lastName', { ns: 'users' })} value={editUser.lastName ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, lastName: e.target.value }))} />
                          <Input label={t('fields.email', { ns: 'users' })} type="email" value={editUser.email ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, email: e.target.value }))} className="sm:col-span-2" />
                          <Input label={t('fields.phoneNumber', { ns: 'users' })} value={editUser.phoneNumber ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, phoneNumber: e.target.value }))} />
                          <Input label={t('fields.nationality', { ns: 'users' })} value={editUser.nationality ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, nationality: e.target.value }))} />
                          <Input label={t('fields.currentLocation', { ns: 'users' })} value={editUser.currentLocation ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, currentLocation: e.target.value }))} className="sm:col-span-2" />
                          <div className="sm:col-span-2 space-y-2">
                            <label className="block text-sm font-medium text-gray-700">{t('fields.profileImageUrl', { ns: 'users' })}</label>
                            <div className="flex items-start gap-4 flex-wrap">
                              {(editUser.profileImageUrl || profileImagePreviewUrl) && (
                                <div className="relative flex-shrink-0">
                                  <img
                                    src={profileImagePreviewUrl || editUser.profileImageUrl || ''}
                                    alt=""
                                    className="h-24 w-24 rounded-lg object-cover border border-gray-200 bg-gray-100"
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                  {profileImageUploading && (
                                    <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30">
                                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="flex flex-col gap-1">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleProfileImageChange}
                                  disabled={profileImageUploading}
                                  className="text-sm text-gray-600 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                                />
                                {(editUser.profileImageUrl || profileImagePreviewUrl) && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditUser((u) => ({ ...u, profileImageUrl: '' }));
                                      if (profileImagePreviewUrl) {
                                        URL.revokeObjectURL(profileImagePreviewUrl);
                                        setProfileImagePreviewUrl(null);
                                      }
                                    }}
                                    disabled={profileImageUploading}
                                    className="text-sm text-red-600 hover:underline disabled:opacity-50"
                                  >
                                    {t('actions.removeImage', { ns: 'users' })}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{t('fields.role', { ns: 'users' })}</label>
                            <Select value={editUser.role ?? ''} onChange={(e) => setEditUser((u) => ({ ...u, role: e.target.value }))} options={[{ value: 'user', label: t('roles.user', { ns: 'users' }) }, { value: 'seller', label: t('roles.seller', { ns: 'users' }) }]} className="w-full" />
                          </div>
                          <div className="flex items-center gap-2">
                            <input type="checkbox" id="verified" checked={editUser.verified ?? false} onChange={(e) => setEditUser((u) => ({ ...u, verified: e.target.checked }))} className="rounded border-gray-300" />
                            <label htmlFor="verified">{t('fields.verified', { ns: 'users' })}</label>
                          </div>
                        </>
                      ) : (
                        <>
                          <div><span className="text-gray-500">{t('fields.firstName', { ns: 'users' })}:</span> {detailsUser.firstName}</div>
                          <div><span className="text-gray-500">{t('fields.lastName', { ns: 'users' })}:</span> {detailsUser.lastName}</div>
                          <div className="sm:col-span-2"><span className="text-gray-500">{t('fields.email', { ns: 'users' })}:</span> {detailsUser.email}</div>
                          <div><span className="text-gray-500">{t('fields.phoneNumber', { ns: 'users' })}:</span> {detailsUser.phoneNumber || '—'}</div>
                          <div><span className="text-gray-500">{t('fields.role', { ns: 'users' })}:</span> {detailsUser.role}</div>
                          <div><span className="text-gray-500">{t('fields.nationality', { ns: 'users' })}:</span> {detailsUser.nationality || '—'}</div>
                          <div><span className="text-gray-500">{t('fields.currentLocation', { ns: 'users' })}:</span> {detailsUser.currentLocation || '—'}</div>
                          <div className="sm:col-span-2"><span className="text-gray-500">{t('fields.verified', { ns: 'users' })}:</span> {detailsUser.verified ? t('status.verified', { ns: 'users' }) : t('filters.unverified', { ns: 'users' })}</div>
                          {detailsUser.profileImageUrl && (
                            <div className="sm:col-span-2">
                              <span className="text-gray-500 block mb-1">{t('fields.profileImageUrl', { ns: 'users' })}:</span>
                              <img src={detailsUser.profileImageUrl} alt="" className="h-24 w-24 rounded-lg object-cover border border-gray-200" />
                            </div>
                          )}
                          <div className="sm:col-span-2"><span className="text-gray-500">{t('fields.joined', { ns: 'users' })}:</span> {formatDate(detailsUser.createdAt)}</div>
                          {detailsUser.banned && (
                            <div className="sm:col-span-2 text-red-600">
                              {t('fields.banned', { ns: 'users' })}: {detailsUser.banReason || '—'}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {(detailsSeller || (editMode && detailsUser?.role === 'seller')) && (
                    <div className="space-y-4">
                      <h3 className="font-medium text-gray-900 border-b pb-2">{t('sections.sellerProfile', { ns: 'users' })}</h3>
                      {detailsSeller ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50/50 p-4 mb-2">
                            <p className="text-sm font-semibold text-amber-900">
                              {t('sections.promotion', { ns: 'users' })}
                            </p>
                            <p className="text-xs text-amber-800 mt-1">
                              {t('sections.promotionHint', { ns: 'users' })}
                            </p>
                          </div>
                          {editMode ? (
                            <>
                              <Input label={t('fields.sellerName', { ns: 'users' })} value={editSeller.name ?? ''} onChange={(e) => setEditSeller((s) => ({ ...s, name: e.target.value }))} />
                              <Input label={t('fields.sellerLogo', { ns: 'users' })} value={editSeller.logo ?? ''} onChange={(e) => setEditSeller((s) => ({ ...s, logo: e.target.value }))} />
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fields.sellerType', { ns: 'users' })}</label>
                                <Select value={editSeller.type ?? ''} onChange={(e) => setEditSeller((s) => ({ ...s, type: e.target.value }))} options={[{ value: 'individual', label: t('sellerType.individual', { ns: 'users' }) }, { value: 'enterprise', label: t('sellerType.enterprise', { ns: 'users' }) }]} className="w-full" />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('fields.status', { ns: 'users' })}</label>
                                <Select value={editSeller.status ?? ''} onChange={(e) => setEditSeller((s) => ({ ...s, status: e.target.value }))} options={[{ value: 'active', label: t('status.active', { ns: 'common' }) }, { value: 'suspended', label: t('sellerStatus.suspended', { ns: 'users' }) }]} className="w-full" />
                              </div>
                              <div className="flex items-center gap-2 sm:col-span-2">
                                <input type="checkbox" id="official" checked={editSeller.official ?? false} onChange={(e) => setEditSeller((s) => ({ ...s, official: e.target.checked }))} className="rounded border-gray-300" />
                                <label htmlFor="official">{t('fields.official', { ns: 'users' })}</label>
                              </div>
                              {!detailsSeller.isPlatformStore && (
                                <>
                                  <div className="sm:col-span-2 border-t pt-4 mt-2">
                                    <p className="text-sm font-medium text-gray-900 mb-3">
                                      {t('sections.promotion', { ns: 'users' })}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 sm:col-span-2">
                                    <input
                                      type="checkbox"
                                      id="promotionActive"
                                      checked={editSeller.promotionActive ?? false}
                                      onChange={(e) =>
                                        setEditSeller((s) => ({
                                          ...s,
                                          promotionActive: e.target.checked,
                                        }))
                                      }
                                      className="rounded border-gray-300"
                                    />
                                    <label htmlFor="promotionActive">
                                      {t('fields.promotionActive', { ns: 'users' })}
                                    </label>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('fields.promotionStartsAt', { ns: 'users' })}
                                    </label>
                                    <input
                                      type="datetime-local"
                                      value={String(editSeller.promotionStartsAt ?? '')}
                                      onChange={(e) =>
                                        setEditSeller((s) => ({
                                          ...s,
                                          promotionStartsAt: e.target.value,
                                        }))
                                      }
                                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('fields.promotionExpiresAt', { ns: 'users' })}
                                    </label>
                                    <input
                                      type="datetime-local"
                                      value={String(editSeller.promotionExpiresAt ?? '')}
                                      onChange={(e) =>
                                        setEditSeller((s) => ({
                                          ...s,
                                          promotionExpiresAt: e.target.value,
                                        }))
                                      }
                                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      {t('fields.promotionTier', { ns: 'users' })}
                                    </label>
                                    <input
                                      type="number"
                                      min={1}
                                      value={editSeller.promotionTier ?? 1}
                                      onChange={(e) =>
                                        setEditSeller((s) => ({
                                          ...s,
                                          promotionTier: parseInt(e.target.value, 10) || 1,
                                        }))
                                      }
                                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                  </div>
                                </>
                              )}
                              {detailsSeller.isPlatformStore && (
                                <p className="sm:col-span-2 text-sm text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">
                                  {t('messages.platformStorePinned', { ns: 'users' })}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <div><span className="text-gray-500">{t('fields.sellerName', { ns: 'users' })}:</span> {detailsSeller.name}</div>
                              <div><span className="text-gray-500">{t('fields.sellerType', { ns: 'users' })}:</span> {detailsSeller.type}</div>
                              <div><span className="text-gray-500">{t('fields.status', { ns: 'users' })}:</span> {detailsSeller.status}</div>
                              <div><span className="text-gray-500">{t('fields.official', { ns: 'users' })}:</span> {detailsSeller.official ? t('common.yes', { ns: 'users' }) : t('common.no', { ns: 'users' })}</div>
                              {detailsSeller.isPlatformStore && (
                                <div className="sm:col-span-2 text-indigo-700 text-sm">
                                  {t('messages.platformStorePinned', { ns: 'users' })}
                                </div>
                              )}
                              {!detailsSeller.isPlatformStore && (
                                <>
                                  <div>
                                    <span className="text-gray-500">{t('fields.promotionActive', { ns: 'users' })}:</span>{' '}
                                    {detailsSeller.promotionActive
                                      ? t('common.yes', { ns: 'users' })
                                      : t('common.no', { ns: 'users' })}
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('fields.promotionStartsAt', { ns: 'users' })}:</span>{' '}
                                    {detailsSeller.promotionStartsAt
                                      ? formatDate(detailsSeller.promotionStartsAt)
                                      : '—'}
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('fields.promotionExpiresAt', { ns: 'users' })}:</span>{' '}
                                    {detailsSeller.promotionExpiresAt
                                      ? formatDate(detailsSeller.promotionExpiresAt)
                                      : '—'}
                                  </div>
                                  <div>
                                    <span className="text-gray-500">{t('fields.promotionTier', { ns: 'users' })}:</span>{' '}
                                    {detailsSeller.promotionTier ?? 1}
                                  </div>
                                </>
                              )}
                              {detailsSeller.logo && <div className="sm:col-span-2"><img src={detailsSeller.logo} alt="" className="h-12 w-12 rounded object-cover" /></div>}
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm">{t('messages.noSellerProfile', { ns: 'users' })}</p>
                      )}
                    </div>
                  )}

                  {/* Reset password - only for users the current admin can edit (not own profile unless SUPER_ADMIN) */}
                  {canEditCurrentUser && (
                  <div className="space-y-4 border-t pt-4">
                    <h3 className="font-medium text-gray-900 border-b pb-2">{t('sections.resetPassword', { ns: 'users' })}</h3>
                    {!showResetPassword ? (
                      <Button variant="outline" size="sm" onClick={() => setShowResetPassword(true)}>
                        {t('actions.resetPassword', { ns: 'users' })}
                      </Button>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fields.newPassword', { ns: 'users' })}</label>
                          <div className="relative">
                            <Input
                              type={showNewPassword ? 'text' : 'password'}
                              value={resetPasswordNew}
                              onChange={(e) => setResetPasswordNew(e.target.value)}
                              placeholder="••••••••"
                              minLength={6}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                              aria-label={showNewPassword ? t('actions.hidePassword', { ns: 'users' }) : t('actions.showPassword', { ns: 'users' })}
                            >
                              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">{t('fields.confirmPassword', { ns: 'users' })}</label>
                          <div className="relative">
                            <Input
                              type={showConfirmPassword ? 'text' : 'password'}
                              value={resetPasswordConfirm}
                              onChange={(e) => setResetPasswordConfirm(e.target.value)}
                              placeholder="••••••••"
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmPassword((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                              aria-label={showConfirmPassword ? t('actions.hidePassword', { ns: 'users' }) : t('actions.showPassword', { ns: 'users' })}
                            >
                              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                          </div>
                        </div>
                        {resetPasswordError && <p className="text-sm text-red-600">{resetPasswordError}</p>}
                        <div className="flex gap-2">
                          <Button onClick={handleResetPassword} disabled={resetPasswordSaving} size="sm">
                            {resetPasswordSaving ? '...' : t('actions.resetPassword', { ns: 'users' })}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setShowResetPassword(false); setResetPasswordNew(''); setResetPasswordConfirm(''); setResetPasswordError(''); }}>
                            {t('buttons.cancel', { ns: 'common' })}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                </>
              ) : (
                <p className="text-gray-500">{t('table.noData', { ns: 'common' })}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ban Modal */}
      {showBanModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedUser.banned ? t('modal.unbanTitle', { ns: 'users' }) : t('modal.banTitle', { ns: 'users' })}
            </h3>
            <p className="text-gray-600 mb-4">
              {selectedUser.banned
                ? t('modal.unbanConfirm', { ns: 'users', name: `${selectedUser.firstName} ${selectedUser.lastName}` })
                : t('modal.banConfirm', { ns: 'users', name: `${selectedUser.firstName} ${selectedUser.lastName}` })
              }
            </p>

            {!selectedUser.banned && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('banReason', { ns: 'users' })}
                </label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="input-field"
                  rows={3}
                  placeholder={t('modal.banReasonPlaceholder', { ns: 'users' })}
                  required
                />
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBanModal(false);
                  setSelectedUser(null);
                  setBanReason('');
                }}
                className="btn-outline"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </button>
              <button
                onClick={() => handleBanUser(selectedUser, !selectedUser.banned)}
                disabled={!selectedUser.banned && !banReason.trim()}
                className={`btn-${selectedUser.banned ? 'primary' : 'secondary'} disabled:opacity-50`}
              >
                {selectedUser.banned ? t('actions.unban', { ns: 'users' }) : t('actions.ban', { ns: 'users' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;